import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button} from "react-bootstrap";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAxiosPrivateFileUpload from "../../../../hooks/useAxiosPrivateFileUpload";
import {toastError, toastSuccess} from "../../../../helpers/Toast";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const EmployeeTrainingDocuments = ({employeeTrainingId}) => {
  const axiosPrivate = useAxiosPrivate();
  const axiosPrivateFileUpload = useAxiosPrivateFileUpload();
  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const persisted = Number(employeeTrainingId) > 0;

  const loadDocuments = useCallback(async () => {
    if (!persisted) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosPrivate.get(`/employee-trainings/${employeeTrainingId}/documents`);
      setDocuments(response.data || []);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać obiegówek szkoleniowych");
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, employeeTrainingId, persisted]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !persisted) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toastError("Dozwolone są pliki PDF, JPG i PNG");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toastError("Plik obiegówki nie może być większy niż 10 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setSaving(true);
    try {
      await axiosPrivateFileUpload.post(`/employee-trainings/${employeeTrainingId}/documents`, formData);
      await loadDocuments();
      toastSuccess("Obiegówka szkoleniowa została zapisana");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się zapisać obiegówki szkoleniowej");
    } finally {
      setSaving(false);
    }
  };

  const download = async document => {
    try {
      const response = await axiosPrivate.get(
        `/employee-trainings/${employeeTrainingId}/documents/${document.id}/download`,
        {responseType: "blob"}
      );
      const url = URL.createObjectURL(response.data);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.fileName || "obiegowka-szkoleniowa";
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się pobrać obiegówki szkoleniowej");
    }
  };

  const remove = async document => {
    if (!window.confirm(`Usunąć obiegówkę „${document.fileName}”?`)) return;
    setSaving(true);
    try {
      await axiosPrivate.delete(`/employee-trainings/${employeeTrainingId}/documents/${document.id}`);
      await loadDocuments();
      toastSuccess("Obiegówka szkoleniowa została usunięta");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się usunąć obiegówki szkoleniowej");
    } finally {
      setSaving(false);
    }
  };

  const formatBytes = value => {
    const bytes = Number(value || 0);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!persisted) {
    return (
      <div className="small text-muted mt-2">
        Zapisz pracownika, aby dodać zdjęcie lub skan obiegówki do tego szkolenia.
      </div>
    );
  }

  return (
    <div className="mt-3 border-top pt-2">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div>
          <strong className="small">Obiegówka szkoleniowa</strong>
          <div className="small text-muted">PDF, JPG lub PNG, maksymalnie 10 MB.</div>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            className="d-none"
            onChange={upload}
          />
          <Button type="button" size="sm" variant="outline-primary" disabled={saving} onClick={() => fileInputRef.current?.click()}>
            Dodaj skan / zdjęcie
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="small text-muted">Ładowanie obiegówek…</div>
      ) : documents.length === 0 ? (
        <div className="small text-muted">Brak załączonej obiegówki.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Plik</th>
                <th>Rozmiar</th>
                <th>Przesłano</th>
                <th className="text-end">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(document => (
                <tr key={document.id}>
                  <td>{document.fileName}</td>
                  <td>{formatBytes(document.fileSize)}</td>
                  <td>{document.uploadedAt ? new Date(document.uploadedAt).toLocaleString("pl-PL") : "—"}</td>
                  <td className="text-end text-nowrap">
                    <Button type="button" size="sm" variant="outline-primary" className="me-2" onClick={() => download(document)}>
                      Pobierz
                    </Button>
                    <Button type="button" size="sm" variant="outline-danger" disabled={saving} onClick={() => remove(document)}>
                      Usuń
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmployeeTrainingDocuments;
